import { CircleNotch, LockOpen } from "@phosphor-icons/react";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ResendTokenTimer } from "~/components/ResendTokenTimer";
import { AuthAPI, Language, ResendLoginTokenData } from "~/lib/api/auth";
import {
    IS_PR_TEMPLATE,
    IS_PRF_TEMPLATE,
    IS_TJPR_TEMPLATE,
    IS_TRN_TEMPLATE
} from "~/utils/constants";

interface ResendLoginTokenProps {
  credentials: ResendLoginTokenData;
  initialIsBlocked?: boolean;
}

export const ResendLoginToken: React.FC<ResendLoginTokenProps> = ({
  credentials,
  initialIsBlocked = true,
}) => {
  const { locale } = useRouter();
  const [isBlocked, setIsBlocked] = useState(initialIsBlocked);
  const [isResending, setIsResending] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(() => new Date());

  useEffect(() => {
    if (!isBlocked) return;

    const timeout = setTimeout(
      () => {
        setIsBlocked(false);
      },
      1000 * 60 * 5,
    ); // 5 minutos

    return () => {
      clearTimeout(timeout);
    };
  }, [isBlocked]);

  async function handleResendToken() {
    setIsResending(true);

    const currentLocale = locale || 'pt';
    const languageMap = {
      'pt': Language.PT_BR,
      'en': Language.EN,
      'es': Language.ES,
      'ar': Language.AR
    };

    const dataWithLanguage: ResendLoginTokenData = {
      ...credentials,
      language: languageMap[currentLocale as keyof typeof languageMap]
    };

    try {
      await AuthAPI.resendLoginToken(dataWithLanguage);

      setIsBlocked(true);
      setLastSentAt(new Date());
      toast("Token encaminhado para seu E-mail e Telefone.", {
        type: "success",
        toastId: "resend-login-token",
      });
    } catch {
      toast("Não foi possível enviar o token.", {
        type: "error",
        toastId: "resend-login-token",
      });
    } finally {
      setIsResending(false);
    }
  }
  const t = useTranslations();

  return (
    <div
      className={classNames("flex flex-col items-center gap-1", {
        "bg-app-gray-950": IS_TJPR_TEMPLATE,
      })}
    >
      <button
        type="button"
        aria-busy={isResending}
        onClick={handleResendToken}
        disabled={isResending || isBlocked}
        className={classNames(
          "flex items-center gap-2 p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-blue-500 disabled:cursor-not-allowed hover:enabled:underline",
          {
            "disabled:text-app-gray-500 text-sm": IS_PRF_TEMPLATE,
            "disabled:text-app-gray-900/50 text-xs text-app-gray-900":
              IS_TRN_TEMPLATE,
            "disabled:text-app-gray-200/60 text-xs text-app-gray-200":
              IS_PR_TEMPLATE,
            "disabled:text-app-gray-200/50 text-sm text-app-gray-200":
              !IS_PRF_TEMPLATE,
          },
        )}
      >
        {t("resend-token")}
        {isResending ? (
          <CircleNotch className="animate-spin" size={18} weight="bold" />
        ) : (
          <LockOpen size={18} weight="bold" />
        )}
      </button>

      {isBlocked && (
        <ResendTokenTimer lastSentAt={lastSentAt} timeInMinutes={5} />
      )}
    </div>
  );
};
